/**
 * Client for the AI roster importer (EPIC-95, PMA-INGEST).
 *
 * Follows the same "raw fetch, not the axios apiClient" pattern as
 * `loggerApi.ts`'s media uploads (`uploadTeamLogo`/`uploadPlayerPhoto`):
 * the axios instance in `api.ts` sets a hard `Content-Type: application/json`
 * default header that fights a `FormData` body's auto-computed multipart
 * boundary, so multipart calls route through `fetchLoggerWithAuth` instead,
 * which carries the same auth-header/E2E-bypass/timeout behavior.
 *
 * Endpoint contract (locked, `team_plan.md` "API contract"):
 *   POST /players/roster-import/extract  — multipart (team_id, text?, images[])
 *   POST /players/roster-import/apply    — JSON (team_id, idempotency_key, rows[])
 */
import { fetchLoggerWithAuth, API_URL } from "./loggerApi";
import { LONG_TIMEOUT_MS, DEFAULT_TIMEOUT_MS } from "./httpTimeouts";

export type RosterImportMatchStatus =
  | "new"
  | "on_team"
  | "exists_elsewhere"
  | "ambiguous";

export interface RosterImportCandidate {
  player_id: string;
  name: string;
  birth_date?: string | null;
  country_name?: string | null;
  team_names: string[];
  score: number;
}

export interface RosterImportRow {
  row_id: string;
  name: string;
  position?: string | null;
  jersey_number?: number | null;
  birth_date?: string | null;
  country_name?: string | null;
  match: {
    status: RosterImportMatchStatus;
    candidates: RosterImportCandidate[];
  };
}

export interface RosterImportExtractResponse {
  team_id: string;
  rows: RosterImportRow[];
  warnings: string[];
}

export type RosterImportRowAction = "create" | "link" | "skip";

export interface RosterImportApplyRowInput {
  action: RosterImportRowAction;
  player_id?: string;
  name: string;
  position?: string | null;
  jersey_number?: number | null;
  birth_date?: string | null;
  country_name?: string | null;
}

export interface RosterImportAppliedPlayer {
  player_id: string;
  name: string;
}

export interface RosterImportApplyError {
  row_index: number;
  message: string;
}

export interface RosterImportApplyResponse {
  created: RosterImportAppliedPlayer[];
  linked: RosterImportAppliedPlayer[];
  skipped: number;
  errors: RosterImportApplyError[];
}

/** The backend's known 503 feature-gate codes (team_plan.md's locked contract). */
export type RosterImportDisabledCode = "ai_disabled" | "ai_no_provider_key";

/**
 * Thrown by `extractRoster`/`applyRoster` on any non-ok response. `code` is
 * only populated for the 503 feature-gate shape
 * (`{"detail": {"code", "message"}}`); every other error status
 * (400/403/413/415/502) carries a plain-string `detail`, surfaced as
 * `message` with `code` left undefined.
 */
export class RosterImportApiError extends Error {
  readonly status: number;
  readonly code?: RosterImportDisabledCode;
  constructor(
    message: string,
    status: number,
    code?: RosterImportDisabledCode,
  ) {
    super(message);
    this.name = "RosterImportApiError";
    this.status = status;
    this.code = code;
  }
}

const isDisabledCode = (value: unknown): value is RosterImportDisabledCode =>
  value === "ai_disabled" || value === "ai_no_provider_key";

async function toRosterImportApiError(
  response: Response,
): Promise<RosterImportApiError> {
  let detail: unknown;
  try {
    const data = await response.json();
    detail = data?.detail;
  } catch {
    detail = undefined;
  }

  if (detail && typeof detail === "object") {
    const structured = detail as { code?: unknown; message?: unknown };
    const code = isDisabledCode(structured.code) ? structured.code : undefined;
    const message =
      typeof structured.message === "string"
        ? structured.message
        : response.statusText || `Request failed (${response.status})`;
    return new RosterImportApiError(message, response.status, code);
  }

  const message =
    typeof detail === "string"
      ? detail
      : response.statusText || `Request failed (${response.status})`;
  return new RosterImportApiError(message, response.status);
}

// Mirrors the locked contract's server-enforced limits (team_plan.md) so the
// client can reject an obviously-bad selection before spending a round trip.
export const ROSTER_IMPORT_MAX_IMAGES = 4;
export const ROSTER_IMPORT_MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const ACCEPTED_IMAGE_EXTENSIONS = new Set([".png", ".jpg", ".jpeg", ".webp"]);

function hasAcceptedExtension(fileName: string): boolean {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex === -1) return false;
  return ACCEPTED_IMAGE_EXTENSIONS.has(fileName.slice(dotIndex).toLowerCase());
}

export type RosterImportImageErrorKind = "type" | "size";

/**
 * Client-side mirror of the server's per-image checks (type + 5MB size).
 * Returns null when the file is acceptable. Some browsers report
 * `file.type === ""` for otherwise-valid files (same caveat as
 * `ImageUpload.tsx`'s `validateFile`) — extension is the fallback.
 */
export function validateRosterImportImage(
  file: File,
): RosterImportImageErrorKind | null {
  const isAcceptedType = file.type
    ? ACCEPTED_IMAGE_TYPES.has(file.type)
    : hasAcceptedExtension(file.name);
  if (!isAcceptedType) return "type";
  if (file.size > ROSTER_IMPORT_MAX_IMAGE_BYTES) return "size";
  return null;
}

export interface ExtractRosterInput {
  teamId: string;
  text?: string;
  images?: File[];
}

/**
 * POST /players/roster-import/extract (admin only).
 * LONG_TIMEOUT_MS: the backend forwards the images/text to a vision-capable
 * model provider — same justification as `generateNarrative`/`postAiQuery`
 * in `loggerApi.ts` (httpTimeouts.ts).
 * Throws RosterImportApiError on any non-ok response — callers branch on
 * `.status` (413/415/502) and `.code` (503 ai_disabled/ai_no_provider_key).
 */
export const extractRoster = async ({
  teamId,
  text,
  images,
}: ExtractRosterInput): Promise<RosterImportExtractResponse> => {
  const formData = new FormData();
  formData.append("team_id", teamId);
  const trimmedText = text?.trim();
  if (trimmedText) {
    formData.append("text", trimmedText);
  }
  (images ?? []).forEach((file) => formData.append("images", file));

  const response = await fetchLoggerWithAuth(
    `${API_URL}/players/roster-import/extract`,
    { method: "POST", body: formData },
    LONG_TIMEOUT_MS,
  );
  if (!response.ok) {
    throw await toRosterImportApiError(response);
  }
  return response.json();
};

export interface ApplyRosterInput {
  teamId: string;
  idempotencyKey: string;
  rows: RosterImportApplyRowInput[];
}

/**
 * POST /players/roster-import/apply (admin only). Deterministic — no model
 * call — so it stays on DEFAULT_TIMEOUT_MS unless a backend handler review
 * shows otherwise (see httpTimeouts.ts: LONG_TIMEOUT_MS is reserved for a
 * verified path, not assumed from a handler's name).
 * Re-sending the same `idempotencyKey` + payload is safe: the backend
 * returns the original result and writes nothing new (team_plan.md).
 */
export const applyRoster = async ({
  teamId,
  idempotencyKey,
  rows,
}: ApplyRosterInput): Promise<RosterImportApplyResponse> => {
  const response = await fetchLoggerWithAuth(
    `${API_URL}/players/roster-import/apply`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        team_id: teamId,
        idempotency_key: idempotencyKey,
        rows,
      }),
    },
    DEFAULT_TIMEOUT_MS,
  );
  if (!response.ok) {
    throw await toRosterImportApiError(response);
  }
  return response.json();
};

/** One idempotency key per review session (generated when extraction
 *  succeeds, reused across Apply retries of that same session). */
export function createRosterImportIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID (older WebViews).
  return `roster-import-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
